/* BotPros admin → Preferences → General
 *
 * Personal profile + per-user defaults for the signed-in user. Has 7
 * sub-tabs: Profile (with the embedded HR Profile sections — 9 collapsible
 * cards), Email, Calling (link-out), Calendar, Tasks, Security, Automation.
 *
 * Almost everything except Profile is "Coming soon" / link-out copy
 * because the underlying behaviors live in Microsoft 365 (auth, calendar,
 * email) or haven't been wired yet (workflow editor, task defaults).
 *
 * The five email sub-cards (signatures, system signature, templates, BCC
 * token, scheduled list) are top-level components defined in App.tsx and
 * threaded through as React node props so we don't fork them.
 */
import { useEffect, useRef, useState } from 'react'
import { type HrProfile, type CallingMode, updateMyCallingMode, type RcOauthStatus, fetchRcOauthStatus, disconnectRcOauth } from '../../../api'
import { profileCardEditability } from '../../../lib/profileImpersonationEdit'

const CALLING_MODE_OPTIONS: { v: CallingMode; title: string; sub: string }[] = [
  { v: 'smart', title: 'Smart (recommended)', sub: 'Automatically picks the best option for you: your in-CRM softphone if it is connected, otherwise your desk phone, otherwise just a notification.' },
  { v: 'softphone', title: 'In-CRM softphone', sub: 'Calls ring the softphone right here in the browser (the on-screen phone). Requires connecting your RingCentral account.' },
  { v: 'deskphone', title: 'Desk phone', sub: 'Calls ring your physical RingCentral desk phone. The browser stays quiet; you still get a CRM notification.' },
  { v: 'notify', title: 'Notify only', sub: 'Nothing rings in the browser — you just get a CRM notification for each incoming call.' },
]

// Per-user calling mode selector (migration 124, PUT /api/crm/me/calling-mode).
// Self-contained: loads the current value from /api/me and saves on click, so
// it doesn't need the prefs save-all/dirty-count machinery.
export function CallingModeCard({ bare = false }: { bare?: boolean } = {}) {
  const [mode, setMode] = useState<CallingMode | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState<CallingMode | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  // Capability detection so options can be intelligently enabled/grayed:
  //  - rcMapped: does an admin map this user to a desk-phone extension? (deskphone/smart need one)
  //  - oauthConnected: has the user linked their OWN RingCentral account? (softphone needs this)
  //  - oauthConfigured: does this deployment support per-user connect at all?
  //  - hasMic: does THIS device have a microphone? (softphone needs one)
  const [rcMapped, setRcMapped] = useState<boolean | null>(null)
  const [oauthConnected, setOauthConnected] = useState<boolean | null>(null)
  const [oauthConfigured, setOauthConfigured] = useState<boolean | null>(null)
  const [hasMic, setHasMic] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/api/me', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(me => {
        if (cancelled) return
        const m = String(me?.callingMode || 'smart').toLowerCase()
        setMode((['smart', 'softphone', 'deskphone', 'notify'].includes(m) ? m : 'smart') as CallingMode)
        setRcMapped(me?.rcMapped === true)
      })
      .catch(() => { if (!cancelled) setLoadError(true) })
    return () => { cancelled = true }
  }, [])

  // Microphone presence (best-effort). enumerateDevices lists an audioinput
  // entry when a mic exists even before permission is granted; null = couldn't
  // tell, which we treat as "available" so we never wrongly block softphone.
  useEffect(() => {
    let cancelled = false
    navigator.mediaDevices?.enumerateDevices?.()
      .then(ds => { if (!cancelled) setHasMic(ds.some(d => d.kind === 'audioinput')) })
      .catch(() => { if (!cancelled) setHasMic(null) })
    return () => { cancelled = true }
  }, [])

  // Per-user RingCentral OAuth status — softphone mode rings the in-browser
  // softphone only when the user has linked their OWN account (an admin desk-
  // phone mapping alone can't register a browser endpoint). We treat a
  // connection that needs re-auth as not-connected. null = still loading, which
  // we read optimistically (never wrongly block while the request is in flight).
  useEffect(() => {
    let cancelled = false
    fetchRcOauthStatus()
      .then(s => {
        if (cancelled) return
        setOauthConfigured(s?.configured !== false)
        setOauthConnected(!!s?.connected && !s?.needsReconnect)
      })
      .catch(() => { if (!cancelled) { setOauthConfigured(null); setOauthConnected(false) } })
    return () => { cancelled = true }
  }, [])

  // Per-mode availability + the reason shown when a mode is unavailable.
  const NO_EXT = 'No RingCentral extension is assigned to your account yet — ask an admin to map your extension.'
  const NEED_OAUTH = 'Connect your RingCentral account below to ring the in-CRM softphone.'
  const NO_MIC = 'No microphone detected on this device. Plug in a headset/mic, or use Desk phone mode.'
  const availability = (v: CallingMode): { ok: boolean; reason?: string } => {
    if (v === 'notify') return { ok: true }
    if (v === 'softphone') {
      // Softphone needs the user's OWN linked account first, then a mic.
      if (oauthConnected === false) return { ok: false, reason: NEED_OAUTH }
      if (hasMic === false) return { ok: false, reason: NO_MIC }
      return { ok: true }
    }
    // smart / deskphone need a reachable extension — either an admin desk-phone
    // mapping OR the user's own linked account.
    if (rcMapped === false && oauthConnected !== true) return { ok: false, reason: NO_EXT }
    return { ok: true }
  }

  // No RingCentral at all (no admin mapping AND no linked account, both
  // resolved): the mode picker would be four dead radios, so collapse it into a
  // single "connect first" panel. While either flag is still loading we leave
  // the grid up to avoid a flash of the empty state.
  const noRc = rcMapped === false && oauthConnected === false

  const select = async (next: CallingMode) => {
    if (next === mode || saving || !availability(next).ok) return
    const prev = mode
    setMode(next); setSaving(next); setSaveError(null)
    try {
      await updateMyCallingMode(next)
      setSavedAt(Date.now())
    } catch (e) {
      setMode(prev)
      setSaveError(e instanceof Error ? e.message : 'Could not save — try again.')
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className={bare ? undefined : 'prefs-card'} style={bare ? undefined : { borderLeft: '3px solid #0054a9' }}>
      {!bare && <h2 className="prefs-card-title">How should calls reach you?</h2>}
      {!bare && <p className="prefs-card-desc">Choose where incoming calls ring and how you place outbound calls. This applies to your account only.</p>}
      {loadError && <div style={{ fontSize: 12, color: '#b3261e', marginTop: 8 }}>Couldn't load your current setting — pick one below to set it.</div>}
      {noRc ? (
        <div style={{ marginTop: 12, padding: '14px 16px', borderRadius: 8, border: '1px dashed #cbd5e1', background: '#f8fafc', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
          <strong style={{ color: '#0f172a' }}>RingCentral isn't connected to your account yet.</strong>
          <div style={{ marginTop: 6 }}>
            {oauthConfigured
              ? 'Connect your RingCentral account below to place and receive calls right here in the CRM — or ask an admin to map your desk-phone extension. Once either is set up, you can choose how incoming calls reach you.'
              : 'Ask an admin to map your RingCentral extension to your account. Once it’s mapped, you can choose how incoming calls reach you here.'}
          </div>
        </div>
      ) : (
        <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 12 }}>
        {CALLING_MODE_OPTIONS.map(opt => {
          const selected = mode === opt.v
          const isSaving = saving === opt.v
          const avail = availability(opt.v)
          const disabled = !!saving || !avail.ok
          return (
            <button
              key={opt.v}
              type="button"
              disabled={disabled}
              onClick={() => select(opt.v)}
              title={avail.ok ? undefined : avail.reason}
              style={{
                textAlign: 'left',
                padding: '14px 16px',
                borderRadius: 8,
                border: selected ? '1.5px solid #0054a9' : '1px solid #e5e9f0',
                background: selected ? 'linear-gradient(180deg, #f0f7ff, #e6f0fb)' : (avail.ok ? '#fff' : '#f8fafc'),
                cursor: !avail.ok ? 'not-allowed' : (saving ? 'progress' : 'pointer'),
                transition: 'all 120ms cubic-bezier(0.16, 1, 0.3, 1)',
                boxShadow: selected ? '0 1px 3px rgba(0,84,169,0.08), 0 1px 2px rgba(0,84,169,0.1)' : '0 1px 2px rgba(15,23,42,0.04)',
                opacity: !avail.ok ? 0.55 : (saving && !isSaving ? 0.6 : 1),
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  width: 14, height: 14, borderRadius: '50%',
                  border: selected ? '4px solid #0054a9' : '1.5px solid #cbd5e1',
                  background: '#fff', flexShrink: 0,
                }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{opt.title}</span>
                {isSaving && <span style={{ fontSize: 11, color: '#0054a9', fontWeight: 600 }}>Saving…</span>}
                {!avail.ok && <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .4, marginLeft: 'auto' }}>Unavailable</span>}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, lineHeight: 1.4 }}>{opt.sub}</div>
              {!avail.ok && <div style={{ fontSize: 11, color: '#b45309', marginTop: 6, lineHeight: 1.4 }}>{avail.reason}</div>}
            </button>
          )
        })}
      </div>
      {saveError
        ? <div style={{ fontSize: 12, color: '#b3261e', marginTop: 10 }}>{saveError}</div>
        : (savedAt && !saving ? <div style={{ fontSize: 12, color: '#2e7d32', marginTop: 10 }}>Saved.</div> : null)}
      <p style={{ fontSize: 12, color: '#7c98b6', marginTop: 12, lineHeight: 1.5 }}>
        Softphone mode needs your RingCentral account connected (see "Your RingCentral connection" below). Desk-phone and Notify work as soon as an admin maps your extension.
      </p>
        </>
      )}
    </div>
  )
}

// Per-user RingCentral "Connect your account" card (PR #62 OAuth backend — the
// FE was never added). Links a user's own RC account so the softphone registers
// as THEIR extension and outbound uses THEIR number. Hidden when per-user OAuth
// isn't configured on this deployment.
export function RcConnectCard({ bare = false }: { bare?: boolean } = {}) {
  const [status, setStatus] = useState<RcOauthStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const refresh = () => {
    setLoading(true)
    fetchRcOauthStatus()
      .then(s => { setStatus(s); setErr(null) })
      .catch(() => setErr('Could not load connection status'))
      .finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])
  const disconnect = async () => {
    setBusy(true); setErr(null)
    try { await disconnectRcOauth(); refresh() }
    catch { setErr('Could not disconnect — try again.') }
    finally { setBusy(false) }
  }
  if (!loading && status && !status.configured) return null
  const connected = !!status?.connected
  const needsReconnect = !!status?.needsReconnect
  return (
    <div className={bare ? undefined : 'prefs-card'} style={bare ? undefined : { borderLeft: '3px solid #0054a9' }}>
      {!bare && <h2 className="prefs-card-title">Your RingCentral connection</h2>}
      {!bare && <p className="prefs-card-desc">Connect your own RingCentral account so the in-CRM softphone rings <em>your</em> extension and outbound calls show <em>your</em> number. Required for Softphone mode.</p>}
      {err && <div style={{ fontSize: 12, color: '#b3261e', marginTop: 8 }}>{err}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
        {loading ? (
          <span style={{ fontSize: 13, color: '#64748b' }}>Checking…</span>
        ) : connected && !needsReconnect ? (
          <>
            <span className="hs-status-badge" style={{ background: '#e4f7e8', color: '#2e7d32' }}>
              Connected{status?.extensionNumber ? ` · ext ${status.extensionNumber}` : ''}
            </span>
            <button className="prefs-save-btn" style={{ background: '#fff', color: '#b3261e', border: '1px solid #f0c2c0' }} disabled={busy} onClick={disconnect}>
              {busy ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </>
        ) : (
          <>
            {needsReconnect && <span className="hs-status-badge" style={{ background: '#fff3e0', color: '#b65815' }}>Reconnect needed</span>}
            {/* #/ssoLogin survives the 302 chain (redirects without their own
                fragment inherit the navigation's) and lands RC's login SPA
                directly on its SSO route — no password page. */}
            <button className="prefs-save-btn" onClick={() => window.location.assign('/api/crm/rc/oauth/authorize#/ssoLogin')}>
              {needsReconnect ? 'Reconnect RingCentral' : 'Connect RingCentral'}
            </button>
          </>
        )}
      </div>
      <p style={{ fontSize: 12, color: '#7c98b6', marginTop: 12, lineHeight: 1.5 }}>
        Connecting uses your Microsoft sign-in — no separate password. Your credentials aren't stored — only a secure token so the app can place and receive calls as you.
      </p>
    </div>
  )
}

// One unified, plain-language calling card for the signed-in user. Replaces the
// old two-card layout (mode picker + connect) that conflated two different
// "RingCentral" concepts and confused users ("why is my phone set up if I never
// connected?"). The two layers it explains:
//   1. Desk-phone mapping (rcMapped): an admin links your CRM account to your RC
//      extension. No personal login — it's what makes desk-phone calls ring and
//      log. Most users have ONLY this, which is why nothing felt "connected".
//   2. Per-user connect (rcConnected): you sign into your OWN RC account so the
//      browser becomes a softphone and outbound shows your number. Optional.
// Leads with a plain status line (what already works), then offers the optional
// browser-calling upgrade, and tucks the granular where-do-calls-ring picker
// behind an "Advanced" expander so the default view stays dead simple.
export function CallingSetupCard() {
  const [me, setMe] = useState<
    { rcMapped: boolean; rcExtension: string | null; rcConnected: boolean; rcOauthConfigured: boolean } | null
  >(null)
  const [loadErr, setLoadErr] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch('/api/me', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled) return
        if (!d) { setLoadErr(true); return }
        setMe({
          rcMapped: d.rcMapped === true,
          rcExtension: d.rcExtension ?? null,
          rcConnected: d.rcConnected === true,
          rcOauthConfigured: d.rcOauthConfigured === true,
        })
      })
      .catch(() => { if (!cancelled) setLoadErr(true) })
    return () => { cancelled = true }
  }, [])

  const rcMapped = me?.rcMapped
  const rcConnected = me?.rcConnected
  const ext = me?.rcExtension || null
  const oauthConfigured = me?.rcOauthConfigured
  const hasAnyRc = rcMapped === true || rcConnected === true
  const extSuffix = ext ? ` (ext ${ext})` : ''

  return (
    <div className="prefs-card" style={{ borderLeft: '3px solid #0054a9' }}>
      <h2 className="prefs-card-title">Your phone</h2>

      {/* ---- Status: what already works, in plain language ---- */}
      {me === null && !loadErr ? (
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>Checking your phone setup…</p>
      ) : loadErr ? (
        <p style={{ fontSize: 13, color: '#b3261e', marginTop: 8 }}>Couldn't load your phone setup. Refresh to try again.</p>
      ) : hasAnyRc ? (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 8, background: '#f0faf3', border: '1px solid #cdebd6' }}>
            <span style={{ color: '#2e7d32', fontWeight: 700, fontSize: 15, lineHeight: '20px' }}>✓</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>You're set up for calls</div>
              <div style={{ fontSize: 12.5, color: '#475569', marginTop: 3, lineHeight: 1.5 }}>
                {rcMapped
                  ? <>Calls to your work line{extSuffix} ring your desk phone, and the CRM logs every call automatically. Your team set this up — there's nothing you need to do.</>
                  : <>Your RingCentral account is connected{extSuffix}, so calls reach you in the CRM.</>}
              </div>
            </div>
          </div>
          {rcConnected && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 8, background: '#f0faf3', border: '1px solid #cdebd6' }}>
              <span style={{ color: '#2e7d32', fontWeight: 700, fontSize: 15, lineHeight: '20px' }}>✓</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Browser calling is on</div>
                <div style={{ fontSize: 12.5, color: '#475569', marginTop: 3, lineHeight: 1.5 }}>You can make and take calls right here in the CRM, and outbound calls show your own number.</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 8, background: '#f8fafc', border: '1px dashed #cbd5e1' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>You're not set up for calls yet</div>
          <div style={{ fontSize: 12.5, color: '#475569', marginTop: 4, lineHeight: 1.5 }}>
            {oauthConfigured
              ? 'Connect your RingCentral account below to make and take calls in the CRM — or ask an admin to map your desk-phone extension.'
              : 'Ask an admin to map your RingCentral extension to your account. Once it’s mapped, your desk-phone calls will ring and log here automatically.'}
          </div>
        </div>
      )}

      {/* ---- Optional: connect your own account for browser calling ---- */}
      {oauthConfigured && (
        <>
          <div style={{ height: 1, background: '#eef2f7', margin: '16px 0' }} />
          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>
            {rcConnected
              ? 'Your RingCentral account'
              : <>Want to call from your computer? <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Optional</span></>}
          </div>
          {!rcConnected && (
            <p style={{ fontSize: 12.5, color: '#64748b', margin: '4px 0 0', lineHeight: 1.5 }}>
              Connect your RingCentral account to make and take calls right here in the browser, with your own number on outbound calls.
            </p>
          )}
          <div style={{ marginTop: 10 }}><RcConnectCard bare /></div>
        </>
      )}

      {/* ---- Advanced: granular where-do-calls-ring picker (rarely needed) ---- */}
      {hasAnyRc && (
        <>
          <div style={{ height: 1, background: '#eef2f7', margin: '16px 0' }} />
          <button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            aria-expanded={showAdvanced}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0054a9' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: showAdvanced ? 'rotate(90deg)' : 'none', transition: 'transform 120ms ease' }}><polyline points="9 18 15 12 9 6" /></svg>
            Advanced — choose where calls ring
          </button>
          {showAdvanced && <div style={{ marginTop: 12 }}><CallingModeCard bare /></div>}
        </>
      )}
    </div>
  )
}

type GeneralSubTab = 'profile' | 'email' | 'calling' | 'calendar' | 'tasks' | 'security' | 'automation'
type PrefsTab =
  | 'general' | 'notifications' | 'security' | 'account' | 'audit-log'
  | 'import-export' | 'backup' | 'properties' | 'ai' | 'meetings'
  | 'sales-workspace' | 'organization' | 'permissions' | 'territories'
  | 'lead-routing' | 'saved-views' | 'numbering' | 'inspection-settings'
  | 'privacy' | 'marketplace' | 'enrichment' | 'translations' | 'markets'
  | 'connected-apps' | 'deal-automation' | 'ivr' | 'inboxes' | 'availability-mgmt'
  | 'allow-deny-list' | 'mkt-email' | 'mkt-forms' | 'com-tax' | 'estimation-settings'
  | 'invoice-scanner-settings' | 'document-mgmt-settings'
  | 'n8n-integration' | 'mkt-sending' | 'mkt-social' | 'com-accounting'
  | 'report-defaults' | 'hr-documents' | 'automation-settings' | 'users' | 'calling'

type Props = {
  generalSubTab: GeneralSubTab
  setPrefsTab: (v: PrefsTab) => void
  prefsValues: Record<string, string>
  updatePref: (key: string, value: string) => void
  prefsPhoto: string | null
  prefsPhotoRef: React.RefObject<HTMLInputElement | null>
  prefsPhotoUploading: boolean
  prefsPhotoError: string | null
  handlePrefsPhotoChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Click the current photo → reopen the crop/zoom modal on it */
  onEditPhoto: () => void
  hrProfile: HrProfile | null
  setHrProfile: React.Dispatch<React.SetStateAction<HrProfile | null>>
  hrProfileLoading: boolean
  hrProfileError: string | null
  hrProfileDirty: number
  hrProfileSaving: boolean
  hrCancelAll: () => void
  hrSaveAll: () => void
  hrCollapsed: Set<string>
  toggleHrSection: (section: string) => void
  updateHrField: (section: string, key: string, value: any) => void
  updateHrContacts: (idx: number, key: string, value: string) => void
  updateHrDependents: (idx: number, key: string, value: string) => void
  revealHrPii: (field: string) => Promise<{ value: string | null }>
  formatDatePref: (d: string) => string
  currentUser: { email: string; name: string; role: string; userId: number | null } | null
  /** When a super-admin is acting as another user, this holds that user's
   * identity. The Profile section flips to show this user instead of
   * `currentUser` so admins can audit/verify the impersonated user's profile
   * without leaving the impersonation context. Editing is disabled while
   * impersonating to prevent writing the impersonated user's preferences
   * to the signed-in user's localStorage. */
  impersonating?: { userId: number; email: string; name: string; role: string; adminName?: string; adminEmail?: string } | null
  // Email section components owned by App.tsx — passed in as nodes.
  emailSignaturesNode: React.ReactNode
  systemEmailSignatureNode: React.ReactNode
  emailTemplatesNode: React.ReactNode
  emailBccTokenNode: React.ReactNode
  emailScheduledListNode: React.ReactNode
}

function PrefsSelect(props: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  style?: React.CSSProperties
}) {
  const { value, onChange, options, style } = props
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])
  const selected = options.find((o) => o.value === value)
  return (
    <div className="prefs-dropdown" ref={ref} style={style}>
      <button type="button" className={`prefs-dropdown-trigger${open ? ' open' : ''}`} onClick={() => setOpen(!open)}>
        <span>{selected?.label || value || '—'}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor"><path d="M1 1l4 4 4-4"/></svg>
      </button>
      {open && (
        <div className="prefs-dropdown-panel">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`prefs-dropdown-option${o.value === value ? ' active' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false) }}
            >{o.label}</button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function General(props: Props) {
  const {
    generalSubTab, setPrefsTab,
    prefsValues, updatePref,
    prefsPhoto, prefsPhotoRef, prefsPhotoUploading, prefsPhotoError, handlePrefsPhotoChange, onEditPhoto,
    hrProfile, setHrProfile,
    hrProfileLoading, hrProfileError, hrProfileDirty, hrProfileSaving,
    hrCancelAll, hrSaveAll,
    hrCollapsed, toggleHrSection,
    updateHrField, updateHrContacts, updateHrDependents,
    revealHrPii, formatDatePref,
    currentUser,
    impersonating,
    emailSignaturesNode, systemEmailSignatureNode, emailTemplatesNode, emailBccTokenNode, emailScheduledListNode,
  } = props

  // When impersonating, an admin manages the IMPERSONATED user's profile.
  // Reads + writes target THAT user's HR profile: `hrProfile` is
  // impersonation-scoped (fetchHrProfile carries X-Impersonate-User-Id), and
  // the save paths — PUT /api/hr/profile (name/phone via the HR "Save" bar) and
  // POST /api/hr/profile/photo (photo) — resolve the target by the swapped
  // userEmail. So name/phone bind to `hrProfile` + `updateHrField('_top', …)`,
  // NOT the signed-in admin's CRM prefs (which would write the wrong record and
  // pollute the admin's own localStorage). A normal, non-impersonating user
  // keeps the existing self-edit prefs path. Email is the identity key and is
  // read-only in every mode.
  const displayUser = impersonating
    ? { email: impersonating.email, name: impersonating.name, role: impersonating.role, userId: impersonating.userId }
    : currentUser
  const displayInitials = ((displayUser?.name) || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  const [displayFirstName = '', ...displayRest] = (displayUser?.name || '').trim().split(/\s+/)
  const displayLastName = displayRest.join(' ')
  const editingImpersonated = !!impersonating
  const { nameEditable, photoEditable } = profileCardEditability({
    impersonating: editingImpersonated,
    hrProfileLoaded: !!hrProfile,
  })
  // Impersonating: show/edit the impersonated user's HR-profile values (falling
  // back to their display name until the profile loads). Self: the prefs store.
  const profileFirstName = editingImpersonated ? (hrProfile?.firstName ?? displayFirstName) : prefsValues.firstName
  const profileLastName = editingImpersonated ? (hrProfile?.lastName ?? displayLastName) : prefsValues.lastName
  const profileEmail = displayUser?.email || ''
  const profilePhone = editingImpersonated ? (hrProfile?.phone ?? '') : prefsValues.phone
  // Photo shown: the impersonated user's HR photo while impersonating, else the
  // signed-in user's prefs photo.
  const shownPhoto = editingImpersonated ? (hrProfile?.photoUrl ?? null) : prefsPhoto

  return (
    <>
      {/* ===== Profile sub-tab ===== */}
      {generalSubTab === 'profile' && (
        <>
          <div className="prefs-card">
            <h2 className="prefs-card-title">Profile</h2>
            {editingImpersonated && (
              <p
                className="prefs-impersonation-edit-note"
                style={{ margin: '-2px 0 12px', fontSize: 12.5, fontWeight: 600, color: '#0054a9', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                Editing {displayUser?.name || profileEmail}’s profile
              </p>
            )}
            <div className="prefs-profile-top">
              {shownPhoto ? (
                <img
                  src={shownPhoto}
                  alt="Profile"
                  className="prefs-profile-avatar-img"
                  title={(!editingImpersonated && photoEditable) ? 'Adjust photo' : undefined}
                  style={(!editingImpersonated && photoEditable) ? { cursor: 'pointer' } : undefined}
                  onClick={(!editingImpersonated && photoEditable && !prefsPhotoUploading) ? onEditPhoto : undefined}
                />
              ) : (
                <div className="prefs-profile-avatar">{displayInitials}</div>
              )}
              <div className="prefs-profile-meta">
                <span className="prefs-profile-name">{displayUser?.name || profileEmail || 'Profile'}</span>
                <label
                  className="prefs-edit-photo"
                  title={editingImpersonated && !photoEditable ? 'Loading profile…' : undefined}
                  style={{
                    cursor: (prefsPhotoUploading || !photoEditable) ? 'not-allowed' : 'pointer',
                    opacity: (prefsPhotoUploading || !photoEditable) ? 0.55 : 1,
                  }}
                >
                  {prefsPhotoUploading ? 'Uploading...' : shownPhoto ? 'Change photo' : 'Add photo'}
                  <input
                    ref={prefsPhotoRef}
                    type="file"
                    accept="image/*"
                    disabled={prefsPhotoUploading || !photoEditable}
                    style={{ display: 'none' }}
                    onChange={handlePrefsPhotoChange}
                  />
                </label>
                {prefsPhotoError && <span className="prefs-field-hint" style={{ color: '#b42318' }}>{prefsPhotoError}</span>}
              </div>
            </div>
            <div className="prefs-form-grid">
              <div className="prefs-field">
                <label>First name</label>
                <input type="text" value={profileFirstName} onChange={e => {
                  if (editingImpersonated) updateHrField('_top', 'firstName', e.target.value)
                  else updatePref('firstName', e.target.value)
                }} disabled={!nameEditable} />
              </div>
              <div className="prefs-field">
                <label>Last name</label>
                <input type="text" value={profileLastName} onChange={e => {
                  if (editingImpersonated) updateHrField('_top', 'lastName', e.target.value)
                  else updatePref('lastName', e.target.value)
                }} disabled={!nameEditable} />
              </div>
              <div className="prefs-field prefs-field-full">
                <label>Email address</label>
                <input type="email" value={profileEmail} disabled />
                <span className="prefs-field-hint">{impersonating ? `Email of the user you're acting as.` : 'Contact an admin to change your email address.'}</span>
              </div>
              <div className="prefs-field">
                <label>Personal phone number</label>
                <input type="tel" value={profilePhone} onChange={e => {
                  if (!nameEditable) return
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
                  let formatted = digits
                  if (digits.length > 6) formatted = `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`
                  else if (digits.length > 3) formatted = `${digits.slice(0,3)}-${digits.slice(3)}`
                  if (editingImpersonated) updateHrField('_top', 'phone', formatted)
                  else updatePref('phone', formatted)
                }} placeholder="123-456-7890" maxLength={12} disabled={!nameEditable} />
              </div>
            </div>
          </div>

          {/* HR Profile Sections */}
          {hrProfileLoading && <div className="prefs-card"><p style={{ color: '#64748b', padding: '16px' }}>Loading HR profile...</p></div>}
          {hrProfileError && <div className="prefs-card"><p style={{ color: '#7c98b6', padding: '16px' }}>{hrProfileError.includes('401') || hrProfileError.includes('404') ? 'No HR profile linked to your account. Contact an admin if you believe this is an error.' : `Could not load HR profile: ${hrProfileError}`}</p></div>}
          {hrProfile && (
            <>
              {/* Save/Cancel bar for HR */}
              {hrProfileDirty > 0 && (
                <div className="prefs-save-bar" style={{ marginBottom: 16 }}>
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>{hrProfileDirty} unsaved change{hrProfileDirty > 1 ? 's' : ''}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="prefs-cancel-btn" onClick={hrCancelAll}>Cancel</button>
                    <button className="prefs-save-btn" onClick={hrSaveAll} disabled={hrProfileSaving}>{hrProfileSaving ? 'Saving...' : 'Save'}</button>
                  </div>
                </div>
              )}

              {/* Personal Information */}
              <div className="prefs-card">
                <button className="hr-section-header" onClick={() => toggleHrSection('personal')}>
                  <h3 className="hr-section-title">Personal Information</h3>
                  <span className={`hr-section-chevron${hrCollapsed.has('personal') ? '' : ' expanded'}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </button>
                {!hrCollapsed.has('personal') && (
                  <div className="prefs-form-grid" style={{ marginTop: 16 }}>
                    <div className="prefs-field">
                      <label>Middle Name</label>
                      <input type="text" value={hrProfile.personalInfo?.middleName || ''} onChange={e => updateHrField('personalInfo', 'middleName', e.target.value)} />
                    </div>
                    <div className="prefs-field">
                      <label>Date of Birth</label>
                      <input type="date" value={hrProfile.personalInfo?.dateOfBirth || ''} onChange={e => updateHrField('personalInfo', 'dateOfBirth', e.target.value)} />
                    </div>
                    <div className="prefs-field">
                      <label>Gender</label>
                      <PrefsSelect value={hrProfile.personalInfo?.gender || ''} onChange={v => updateHrField('personalInfo', 'gender', v)} options={[
                        { value: '', label: 'Select' },
                        { value: 'Female', label: 'Female' },
                        { value: 'Male', label: 'Male' },
                      ]} />
                    </div>
                    <div className="prefs-field">
                      <label>Marital Status</label>
                      <PrefsSelect value={hrProfile.personalInfo?.maritalStatus || ''} onChange={v => updateHrField('personalInfo', 'maritalStatus', v)} options={[
                        { value: '', label: 'Select' },
                        { value: 'Single', label: 'Single' },
                        { value: 'Married', label: 'Married' },
                        { value: 'Divorced', label: 'Divorced' },
                        { value: 'Widowed', label: 'Widowed' },
                        { value: 'Separated', label: 'Separated' },
                      ]} />
                    </div>
                    <div className="prefs-field">
                      <label>Citizenship</label>
                      <PrefsSelect value={hrProfile.personalInfo?.citizenship || ''} onChange={v => updateHrField('personalInfo', 'citizenship', v)} options={[
                        { value: '', label: 'Select' },
                        { value: 'US Citizen', label: 'US Citizen' },
                        { value: 'Permanent Resident', label: 'Permanent Resident' },
                        { value: 'Work Visa', label: 'Work Visa' },
                        { value: 'Other', label: 'Other' },
                      ]} />
                    </div>
                    <div className="prefs-field">
                      <label>Veteran Status</label>
                      <input type="text" value={hrProfile.personalInfo?.veteran || ''} onChange={e => updateHrField('personalInfo', 'veteran', e.target.value)} />
                    </div>
                    <div className="prefs-field prefs-field-full">
                      <label>Street Address</label>
                      <input type="text" value={hrProfile.personalInfo?.address?.street || ''} onChange={e => {
                        const addr = { ...(hrProfile.personalInfo?.address || {}), street: e.target.value }
                        updateHrField('personalInfo', 'address', addr)
                      }} />
                    </div>
                    <div className="prefs-field">
                      <label>City</label>
                      <input type="text" value={hrProfile.personalInfo?.address?.city || ''} onChange={e => {
                        const addr = { ...(hrProfile.personalInfo?.address || {}), city: e.target.value }
                        updateHrField('personalInfo', 'address', addr)
                      }} />
                    </div>
                    <div className="prefs-field">
                      <label>State</label>
                      <input type="text" value={hrProfile.personalInfo?.address?.state || ''} onChange={e => {
                        const addr = { ...(hrProfile.personalInfo?.address || {}), state: e.target.value }
                        updateHrField('personalInfo', 'address', addr)
                      }} maxLength={2} />
                    </div>
                    <div className="prefs-field">
                      <label>ZIP Code</label>
                      <input type="text" value={hrProfile.personalInfo?.address?.zip || ''} onChange={e => {
                        const addr = { ...(hrProfile.personalInfo?.address || {}), zip: e.target.value }
                        updateHrField('personalInfo', 'address', addr)
                      }} maxLength={10} />
                    </div>
                  </div>
                )}
              </div>

              {/* Emergency Contacts */}
              <div className="prefs-card">
                <button className="hr-section-header" onClick={() => toggleHrSection('emergency')}>
                  <h3 className="hr-section-title">Emergency Contacts</h3>
                  <span className={`hr-section-chevron${hrCollapsed.has('emergency') ? '' : ' expanded'}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </button>
                {!hrCollapsed.has('emergency') && (
                  <div style={{ marginTop: 16 }}>
                    {(hrProfile.emergencyContacts || [{ name: null, phone: null, relationship: null }]).map((contact: any, idx: number) => (
                      <div className="hr-repeater-row" key={idx}>
                        <div className="prefs-field">
                          <label>Name</label>
                          <input type="text" value={contact.name || ''} onChange={e => updateHrContacts(idx, 'name', e.target.value)} />
                        </div>
                        <div className="prefs-field">
                          <label>Phone</label>
                          <input type="tel" value={contact.phone || ''} onChange={e => updateHrContacts(idx, 'phone', e.target.value)} />
                        </div>
                        <div className="prefs-field">
                          <label>Relationship</label>
                          <input type="text" value={contact.relationship || ''} onChange={e => updateHrContacts(idx, 'relationship', e.target.value)} />
                        </div>
                      </div>
                    ))}
                    <button className="hr-add-btn" onClick={() => updateHrContacts((hrProfile.emergencyContacts || []).length, 'name', '')}>
                      + Add Contact
                    </button>
                  </div>
                )}
              </div>

              {/* Employment Details */}
              <div className="prefs-card">
                <button className="hr-section-header" onClick={() => toggleHrSection('employment')}>
                  <h3 className="hr-section-title">Employment Details</h3>
                  <span className={`hr-section-chevron${hrCollapsed.has('employment') ? '' : ' expanded'}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </button>
                {!hrCollapsed.has('employment') && (
                  <div className="prefs-form-grid" style={{ marginTop: 16 }}>
                    <div className="prefs-field">
                      <label>Position</label>
                      <input type="text" value={hrProfile.position || ''} disabled />
                    </div>
                    <div className="prefs-field">
                      <label>Department</label>
                      <input type="text" value={hrProfile.department || ''} disabled />
                    </div>
                    <div className="prefs-field">
                      <label>Employment Type</label>
                      <PrefsSelect value={hrProfile.employmentInfo?.employmentType || ''} onChange={() => {}} options={[
                        { value: '', label: 'Select' },
                        { value: 'Full-Time', label: 'Full-Time' },
                        { value: 'Part-Time', label: 'Part-Time' },
                        { value: 'Contract', label: 'Contract' },
                        { value: 'Seasonal', label: 'Seasonal' },
                      ]} />
                    </div>
                    <div className="prefs-field">
                      <label>Shift</label>
                      <input type="text" value={hrProfile.employmentInfo?.shift || ''} disabled />
                    </div>
                    <div className="prefs-field">
                      <label>Hire Date</label>
                      <input type="text" value={hrProfile.employmentInfo?.hireDate ? formatDatePref(hrProfile.employmentInfo.hireDate) : ''} disabled />
                    </div>
                    <div className="prefs-field">
                      <label>Pay Rate</label>
                      <input type="text" value={hrProfile.employmentInfo?.payRate || ''} disabled />
                    </div>
                    <div className="prefs-field">
                      <label>Subsidiary</label>
                      <PrefsSelect value={hrProfile.employmentInfo?.subsidiary || ''} onChange={() => {}} options={[
                        { value: '', label: 'Select' },
                        { value: 'BotPros', label: 'BotPros' },
                        { value: 'BotPros', label: 'BotPros' },
                        { value: 'BotPros Windows & Doors', label: 'BotPros Windows & Doors' },
                      ]} />
                    </div>
                    <div className="prefs-field">
                      <label>WC Code</label>
                      <input type="text" value={hrProfile.employmentInfo?.wcCode || ''} disabled />
                    </div>
                    <div className="prefs-field">
                      <label>Benefits Eligible</label>
                      <input type="text" value={hrProfile.employmentInfo?.benefitsEligible ? 'Yes' : 'No'} disabled />
                    </div>
                  </div>
                )}
              </div>

              {/* Tax & Payroll */}
              <div className="prefs-card">
                <button className="hr-section-header" onClick={() => toggleHrSection('tax')}>
                  <h3 className="hr-section-title">Tax &amp; Payroll</h3>
                  <span className={`hr-section-chevron${hrCollapsed.has('tax') ? '' : ' expanded'}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </button>
                {!hrCollapsed.has('tax') && (
                  <div className="prefs-form-grid" style={{ marginTop: 16 }}>
                    <div className="prefs-field">
                      <label>Tax Filing Status</label>
                      <PrefsSelect value={hrProfile.taxInfo?.taxFilingStatus || ''} onChange={v => updateHrField('taxInfo', 'taxFilingStatus', v)} options={[
                        { value: '', label: 'Select' },
                        { value: 'Single', label: 'Single' },
                        { value: 'Married Filing Jointly', label: 'Married Filing Jointly' },
                        { value: 'Married Filing Separately', label: 'Married Filing Separately' },
                        { value: 'Head of Household', label: 'Head of Household' },
                      ]} />
                    </div>
                    <div className="prefs-field">
                      <label>W-4 Exemptions</label>
                      <input type="text" value={hrProfile.taxInfo?.w4Exemptions || ''} onChange={e => updateHrField('taxInfo', 'w4Exemptions', e.target.value)} />
                    </div>
                    <div className="prefs-field">
                      <label>Additional Federal Withholding</label>
                      <input type="text" value={hrProfile.taxInfo?.additionalFedWithhold || ''} onChange={e => updateHrField('taxInfo', 'additionalFedWithhold', e.target.value)} />
                    </div>
                    <div className="prefs-field">
                      <label>Additional State Withholding</label>
                      <input type="text" value={hrProfile.taxInfo?.additionalStateWithhold || ''} onChange={e => updateHrField('taxInfo', 'additionalStateWithhold', e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              {/* Banking & Direct Deposit */}
              <div className="prefs-card">
                <button className="hr-section-header" onClick={() => toggleHrSection('banking')}>
                  <h3 className="hr-section-title">Banking &amp; Direct Deposit</h3>
                  <span className={`hr-section-chevron${hrCollapsed.has('banking') ? '' : ' expanded'}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </button>
                {!hrCollapsed.has('banking') && (
                  <div className="prefs-form-grid" style={{ marginTop: 16 }}>
                    <div className="prefs-field">
                      <label>Bank Name</label>
                      <input type="text" value={hrProfile.bankingInfo?.bankName || ''} onChange={e => updateHrField('bankingInfo', 'bankName', e.target.value)} />
                    </div>
                    <div className="prefs-field">
                      <label>Account Holder Name</label>
                      <input type="text" value={hrProfile.bankingInfo?.bankAccountName || ''} onChange={e => updateHrField('bankingInfo', 'bankAccountName', e.target.value)} />
                    </div>
                    <div className="prefs-field">
                      <label>Account Type</label>
                      <PrefsSelect value={hrProfile.bankingInfo?.bankAccountType || ''} onChange={v => updateHrField('bankingInfo', 'bankAccountType', v)} options={[
                        { value: '', label: 'Select' },
                        { value: 'Checking', label: 'Checking' },
                        { value: 'Savings', label: 'Savings' },
                      ]} />
                    </div>
                    <div className="prefs-field">
                      <label>Routing Number</label>
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <input type="text" value={hrProfile.bankRouting || '****'} readOnly style={{ flex: 1 }} />
                        <button className="hr-reveal-btn" onClick={() => revealHrPii('bankRouting').then(r => { if (r.value) setHrProfile(prev => prev ? { ...prev, bankRouting: r.value as string } : prev) })}>Reveal</button>
                      </span>
                    </div>
                    <div className="prefs-field">
                      <label>Account Number</label>
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <input type="text" value={hrProfile.bankAccount || '****'} readOnly style={{ flex: 1 }} />
                        <button className="hr-reveal-btn" onClick={() => revealHrPii('bankAccount').then(r => { if (r.value) setHrProfile(prev => prev ? { ...prev, bankAccount: r.value as string } : prev) })}>Reveal</button>
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Education & Skills */}
              <div className="prefs-card">
                <button className="hr-section-header" onClick={() => toggleHrSection('education')}>
                  <h3 className="hr-section-title">Education &amp; Skills</h3>
                  <span className={`hr-section-chevron${hrCollapsed.has('education') ? '' : ' expanded'}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </button>
                {!hrCollapsed.has('education') && (
                  <div className="prefs-form-grid" style={{ marginTop: 16 }}>
                    <div className="prefs-field">
                      <label>Education Level</label>
                      <input type="text" value={hrProfile.educationInfo?.educationLevel || ''} onChange={e => updateHrField('educationInfo', 'educationLevel', e.target.value)} />
                    </div>
                    <div className="prefs-field">
                      <label>School</label>
                      <input type="text" value={hrProfile.educationInfo?.school || ''} onChange={e => updateHrField('educationInfo', 'school', e.target.value)} />
                    </div>
                    <div className="prefs-field">
                      <label>Graduation Year</label>
                      <input type="text" value={hrProfile.educationInfo?.graduationYear || ''} onChange={e => updateHrField('educationInfo', 'graduationYear', e.target.value)} />
                    </div>
                    <div className="prefs-field prefs-field-full">
                      <label>Skills</label>
                      <input type="text" value={hrProfile.educationInfo?.skills || ''} onChange={e => updateHrField('educationInfo', 'skills', e.target.value)} />
                    </div>
                    <div className="prefs-field prefs-field-full">
                      <label>Certifications</label>
                      <input type="text" value={hrProfile.educationInfo?.certifications || ''} onChange={e => updateHrField('educationInfo', 'certifications', e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              {/* Dependents */}
              <div className="prefs-card">
                <button className="hr-section-header" onClick={() => toggleHrSection('dependents')}>
                  <h3 className="hr-section-title">Dependents</h3>
                  <span className={`hr-section-chevron${hrCollapsed.has('dependents') ? '' : ' expanded'}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </button>
                {!hrCollapsed.has('dependents') && (
                  <div style={{ marginTop: 16 }}>
                    {(hrProfile.dependents || []).length === 0 && <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No dependents added.</p>}
                    {(hrProfile.dependents || []).map((dep: any, idx: number) => (
                      <div className="hr-repeater-row" key={idx} style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <div className="prefs-field">
                          <label>Name</label>
                          <input type="text" value={dep.name || ''} onChange={e => updateHrDependents(idx, 'name', e.target.value)} />
                        </div>
                        <div className="prefs-field">
                          <label>Relationship</label>
                          <input type="text" value={dep.relationship || ''} onChange={e => updateHrDependents(idx, 'relationship', e.target.value)} />
                        </div>
                      </div>
                    ))}
                    <button className="hr-add-btn" onClick={() => updateHrDependents((hrProfile.dependents || []).length, 'name', '')}>
                      + Add Dependent
                    </button>
                  </div>
                )}
              </div>

              {/* Documents & Compliance */}
              <div className="prefs-card">
                <button className="hr-section-header" onClick={() => toggleHrSection('documents')}>
                  <h3 className="hr-section-title">Documents &amp; Compliance</h3>
                  <span className={`hr-section-chevron${hrCollapsed.has('documents') ? '' : ' expanded'}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </button>
                {!hrCollapsed.has('documents') && (
                  <div className="prefs-form-grid" style={{ marginTop: 16 }}>
                    <div className="prefs-field">
                      <label>Safety Training</label>
                      <input type="text" value={hrProfile.trainingInfo?.safetyTraining || 'Not completed'} readOnly />
                    </div>
                    <div className="prefs-field">
                      <label>Safety Training Date</label>
                      <input type="text" value={hrProfile.trainingInfo?.safetyTrainingDate || '--'} readOnly />
                    </div>
                    <div className="prefs-field">
                      <label>Harassment Training</label>
                      <input type="text" value={hrProfile.trainingInfo?.harassmentTraining || 'Not completed'} readOnly />
                    </div>
                    <div className="prefs-field">
                      <label>Harassment Training Date</label>
                      <input type="text" value={hrProfile.trainingInfo?.harassmentTrainingDate || '--'} readOnly />
                    </div>
                    <div className="prefs-field">
                      <label>Drug Test</label>
                      <input type="text" value={hrProfile.trainingInfo?.drugTest || 'Not completed'} readOnly />
                    </div>
                    <div className="prefs-field">
                      <label>Drug Test Date</label>
                      <input type="text" value={hrProfile.trainingInfo?.drugTestDate || '--'} readOnly />
                    </div>
                  </div>
                )}
              </div>

              {/* Immigration & Licensing */}
              <div className="prefs-card">
                <button className="hr-section-header" onClick={() => toggleHrSection('immigration')}>
                  <h3 className="hr-section-title">Immigration &amp; Licensing</h3>
                  <span className={`hr-section-chevron${hrCollapsed.has('immigration') ? '' : ' expanded'}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </button>
                {!hrCollapsed.has('immigration') && (
                  <div className="prefs-form-grid" style={{ marginTop: 16 }}>
                    <div className="prefs-field">
                      <label>Work Visa</label>
                      <input type="text" value={hrProfile.licensingInfo?.workVisa || ''} onChange={e => updateHrField('licensingInfo', 'workVisa', e.target.value)} />
                    </div>
                    <div className="prefs-field">
                      <label>Visa Type</label>
                      <input type="text" value={hrProfile.licensingInfo?.visaType || ''} onChange={e => updateHrField('licensingInfo', 'visaType', e.target.value)} />
                    </div>
                    <div className="prefs-field">
                      <label>Visa Expiry</label>
                      <input type="date" value={hrProfile.licensingInfo?.visaExpiry || ''} onChange={e => updateHrField('licensingInfo', 'visaExpiry', e.target.value)} />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="prefs-card">
            <h2 className="prefs-card-title">Language &amp; region</h2>
            <div className="prefs-form-grid">
              <div className="prefs-field">
                <label>Language</label>
                <PrefsSelect value={prefsValues.language} onChange={v => updatePref('language', v)} options={[
                  { value: 'en', label: 'English' },
                  { value: 'es', label: 'Spanish' },
                ]} />
              </div>
              <div className="prefs-field">
                <label>Date format</label>
                <PrefsSelect value={prefsValues.dateFormat} onChange={v => updatePref('dateFormat', v)} options={[
                  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
                ]} />
              </div>
              <div className="prefs-field">
                <label>Time format</label>
                <PrefsSelect value={prefsValues.timeFormat} onChange={v => updatePref('timeFormat', v)} options={[
                  { value: '12h', label: '12 hour (1:00 PM)' },
                  { value: '24h', label: '24 hour (13:00)' },
                ]} />
              </div>
              <div className="prefs-field">
                <label>Number format</label>
                <PrefsSelect value={prefsValues.numberFormat} onChange={v => updatePref('numberFormat', v)} options={[
                  { value: '1,234.56', label: '1,234.56' },
                  { value: '1.234,56', label: '1.234,56' },
                ]} />
              </div>
            </div>
          </div>

          <div className="prefs-card">
            <h2 className="prefs-card-title">Defaults</h2>
            <div className="prefs-form-grid">
              <div className="prefs-field">
                <label>Home page</label>
                <PrefsSelect value={prefsValues.homePage} onChange={v => updatePref('homePage', v)} options={[
                  { value: 'home', label: 'Home' },
                  { value: 'deals', label: 'Deals' },
                  { value: 'contacts', label: 'Contacts' },
                ]} />
              </div>
              <div className="prefs-field">
                <label>Pipeline</label>
                <PrefsSelect value={prefsValues.pipeline} onChange={v => updatePref('pipeline', v)} options={[
                  { value: 'estimation', label: 'Estimation Pipeline' },
                  { value: 'workorder', label: 'Work Order Pipeline' },
                  { value: 'subcontractor', label: 'Subcontractor Pipeline' },
                  { value: 'doorknocking', label: 'Door-Knocking Pipeline' },
                ]} />
              </div>
            </div>
            <div className="prefs-working-hours">
              <h3 className="prefs-sub-title">Working hours</h3>
              <p className="prefs-sub-desc">Set the hours you're typically available for meetings and tasks.</p>
              <div className="prefs-hours-row">
                <PrefsSelect value={prefsValues.workDays} onChange={v => updatePref('workDays', v)} options={[
                  { value: 'monday', label: 'Monday – Friday' },
                  { value: 'all', label: 'Every day' },
                ]} />
                <div className="prefs-hours-range">
                  <PrefsSelect value={prefsValues.workStart} onChange={v => updatePref('workStart', v)} options={[
                    { value: '07:00', label: '7:00 AM' },
                    { value: '08:00', label: '8:00 AM' },
                    { value: '09:00', label: '9:00 AM' },
                  ]} />
                  <span className="prefs-hours-to">to</span>
                  <PrefsSelect value={prefsValues.workEnd} onChange={v => updatePref('workEnd', v)} options={[
                    { value: '16:00', label: '4:00 PM' },
                    { value: '17:00', label: '5:00 PM' },
                    { value: '18:00', label: '6:00 PM' },
                  ]} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ===== Email sub-tab =====
          "Connect personal email", "Add alias", "Manage signatures", "Never Log", and the
          emailUnsubLink toggle were all stubs/placebos. The CRM doesn't have a personal-
          mailbox connector, alias manager, signature manager, or marketing-email send path
          that honors unsubscribe preferences. Transactional email (Teams fallback, password
          resets, etc.) goes out via the Worker's email binding, not a user-connected inbox. */}
      {generalSubTab === 'email' && (
        <>
          <div className="prefs-card">
            <h2 className="prefs-card-title">Your mailbox</h2>
            <p className="prefs-card-desc">BotPros Ops uses your Google Workspace mailbox for CRM email. Outbound messages come from {currentUser?.email || 'mritchie@botpros.ai'} via Gmail — no additional connection required.</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
              <span className="hs-status-badge" style={{ background: '#e4f7e8', color: '#2e7d32' }}>Connected via SSO</span>
              <span style={{ color: '#7c98b6', fontSize: '12px' }}>Aliases are managed in Google Workspace. Signatures live below.</span>
            </div>
          </div>

          {emailSignaturesNode}

          {systemEmailSignatureNode}

          {emailTemplatesNode}

          {emailBccTokenNode}

          {emailScheduledListNode}

          <div className="prefs-card">
            <h2 className="prefs-card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Marketing email controls
              <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: '#fff3e0', color: '#b65815', border: '1px solid #ffd9a8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Coming soon
              </span>
            </h2>
            <div style={{ fontSize: '12px', color: '#7c98b6', padding: '8px 10px', background: '#f5f8fa', borderLeft: '3px solid #ffd9a8', borderRadius: '3px' }}>
              Unsubscribe link insertion, "Never Log" allowlists, and marketing blast tooling ship with the Marketing module. The CRM today only sends transactional mail.
            </div>
          </div>
        </>
      )}

      {/* ===== Calling sub-tab =====
          This mirrored the top-level Calling tab but with placebo controls (no phone-number
          registration flow, "Add hours" does nothing, callingRingPref / callingTranscriptLang
          saved to user_preferences without consumers). The real RingCentral admin settings
          live at Preferences > Calling. Point admins there. */}
      {generalSubTab === 'calling' && (
        <>
          <CallingSetupCard />
          <div className="prefs-card">
            <h2 className="prefs-card-title">RingCentral admin settings</h2>
            <p className="prefs-card-desc">Phone-number mappings, inbound routing rules, blocked numbers, call outcomes, and AI are configured system-wide in the RingCentral admin tab.</p>
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button className="prefs-save-btn" onClick={() => setPrefsTab('calling')}>
                Open RingCentral settings
              </button>
              <span style={{ color: '#7c98b6', fontSize: '12px' }}>Admin-only: phone mappings, inbound routing, blocked numbers, AI.</span>
            </div>
          </div>
        </>
      )}

      {/* ===== Calendar sub-tab =====
          calendarSync / calendarScheduling / meetingsUrl / defaultMeetingLink all wrote to
          user_preferences with no consumer. Max's calendar is wired via the legacy read-only
          calendar proxy, and the Teams meeting scheduler on deals uses Graph API — neither
          reads these prefs. Replace with honest status card + link to Meetings tab
          (which has the real scheduler). */}
      {generalSubTab === 'calendar' && (
        <>
          <div className="prefs-card">
            <h2 className="prefs-card-title">Calendar connection</h2>
            <p className="prefs-card-desc">BotPros Ops reads your Microsoft 365 calendar via Microsoft Graph (signed-in user). Only Max's calendar is writable today.</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
              <span className="hs-status-badge" style={{ background: '#e4f7e8', color: '#2e7d32' }}>Connected via SSO</span>
              <span style={{ color: '#7c98b6', fontSize: '12px' }}>No manual setup required.</span>
            </div>
          </div>

          <div className="prefs-card">
            <h2 className="prefs-card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Meeting scheduling pages
              <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: '#fff3e0', color: '#b65815', border: '1px solid #ffd9a8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Coming soon
              </span>
            </h2>
            <div style={{ fontSize: '12px', color: '#7c98b6', padding: '8px 10px', background: '#f5f8fa', borderLeft: '3px solid #ffd9a8', borderRadius: '3px' }}>
              Public booking pages (jwm.botpros.ai/max) require a dedicated scheduling surface. Until then, use the "Schedule Teams meeting" action on any deal record — it creates a real Teams meeting on Max's calendar.
            </div>
          </div>
        </>
      )}

      {/* ===== Tasks sub-tab =====
          taskDefault{DueDate,DueTime,Reminder} and taskFollowUp{Call,Email} wrote to
          user_preferences but no task-create flow reads them. Replace with honest
          placeholder until the task creation modal is wired to consume these defaults. */}
      {generalSubTab === 'tasks' && (
        <div className="prefs-card">
          <h2 className="prefs-card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            Task defaults
            <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: '#fff3e0', color: '#b65815', border: '1px solid #ffd9a8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Coming soon
            </span>
          </h2>
          <div style={{ fontSize: '12px', color: '#7c98b6', padding: '8px 10px', background: '#f5f8fa', borderLeft: '3px solid #ffd9a8', borderRadius: '3px' }}>
            Default due date, due time, reminder, and auto-prompt for follow-up tasks after calls/emails aren't wired yet. The task-creation flow doesn't read these preferences — enabling them requires plumbing through the quick-create task modal.
          </div>
        </div>
      )}

      {/* ===== Security sub-tab =====
          BotPros Ops auth is 100% SSO via Cloudflare Access + Microsoft Entra.
          Passwords, passkeys, 2FA, and session termination are managed by IT in Entra —
          not in-app. Every button here used to be a dead stub. Replace with honest
          status cards + outbound links to the identity management portal. */}
      {generalSubTab === 'security' && (
        <>
          <div className="prefs-card">
            <h2 className="prefs-card-title">Sign-in method</h2>
            <p className="prefs-card-desc">BotPros Ops uses Microsoft Entra SSO via Cloudflare Access. Your identity, password, MFA, and passkeys are managed in your Microsoft 365 account.</p>
            <div className="prefs-sec-row" style={{ marginTop: '12px' }}>
              <label className="prefs-sec-label">Signed in as</label>
              <div className="prefs-sec-value-row">
                <input type="text" readOnly value="mritchie@botpros.ai" className="prefs-sec-readonly" />
                <span className="hs-status-badge" style={{ background: '#e4f7e8', color: '#2e7d32' }}>SSO active</span>
              </div>
            </div>
          </div>

          <div className="prefs-card">
            <h2 className="prefs-card-title">Password, passkeys &amp; MFA</h2>
            <p className="prefs-card-desc">Managed by Microsoft. Updates to your password, passkeys, and multi-factor authentication apply across every BotPros app (Ops, HR Center, Teams, Outlook, etc.).</p>
            <div style={{ marginTop: '12px' }}>
              <a className="prefs-save-btn" href="https://mysignins.microsoft.com/security-info" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', textDecoration: 'none' }}>
                Open Microsoft Security Info
              </a>
            </div>
          </div>

          <div className="prefs-card">
            <h2 className="prefs-card-title">Sign out everywhere</h2>
            <p className="prefs-card-desc">To end all active sessions across BotPros apps, sign out of Microsoft 365 from the My Account portal.</p>
            <div style={{ marginTop: '12px' }}>
              <a className="prefs-link-btn" href="https://myaccount.microsoft.com/device-list" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                Manage signed-in devices →
              </a>
            </div>
          </div>
        </>
      )}

      {/* ===== Automation sub-tab =====
          autoMinimap / autoZoomLevel were personal prefs for a workflow editor that
          doesn't exist yet (there's no /workflows view wired). Point to the future
          surface rather than pretend these toggles do anything. */}
      {generalSubTab === 'automation' && (
        <div className="prefs-card">
          <h2 className="prefs-card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            Workflow editor
            <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: '#fff3e0', color: '#b65815', border: '1px solid #ffd9a8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Coming soon
            </span>
          </h2>
          <div style={{ fontSize: '12px', color: '#7c98b6', padding: '8px 10px', background: '#f5f8fa', borderLeft: '3px solid #ffd9a8', borderRadius: '3px' }}>
            The visual workflow editor (triggers, branches, actions) isn't shipped yet. Personal preferences for it (minimap, zoom) will land when the editor ships.
          </div>
        </div>
      )}
    </>
  )
}
